/**
 * Sanctions Node Config Panel
 *
 * Displayed when a Sanctions Screening node is selected in the workflow builder.
 * Allows per-workflow override of global sanctions settings:
 * - Dataset selection (4 groups, 16 datasets)
 * - Match threshold slider
 * - Name/DOB/Country weighting controls
 */

import React, { useState } from 'react';
import { DATASET_GROUPS, DEFAULT_DATASETS } from '../../constants/screening';

export default function SanctionsNodeConfig({ node, onUpdate }) {
  const config = node.data.config || {};
  const [overrideGlobal, setOverrideGlobal] = useState(config.override_global || false);
  const [datasets, setDatasets] = useState(config.datasets || [...DEFAULT_DATASETS]);
  const [threshold, setThreshold] = useState(config.threshold || 70);
  const [weighting, setWeighting] = useState(config.weighting || { name: 60, dob: 25, country: 15 });

  const updateConfig = (updates) => {
    const newConfig = {
      override_global: overrideGlobal,
      datasets,
      threshold,
      weighting,
      ...updates,
    };
    onUpdate({
      ...node,
      data: {
        ...node.data,
        config: { ...node.data.config, ...newConfig },
      },
    });
  };

  const handleOverrideToggle = (e) => {
    const enabled = e.target.checked;
    setOverrideGlobal(enabled);
    updateConfig({ override_global: enabled });
  };

  const handleDatasetToggle = (datasetId) => {
    const updated = datasets.includes(datasetId)
      ? datasets.filter((d) => d !== datasetId)
      : [...datasets, datasetId];
    setDatasets(updated);
    updateConfig({ datasets: updated });
  };

  const handleThresholdChange = (e) => {
    const val = parseInt(e.target.value);
    setThreshold(val);
    updateConfig({ threshold: val });
  };

  const handleWeightChange = (field, value) => {
    const val = Math.max(0, Math.min(100, parseInt(value) || 0));
    const updated = { ...weighting, [field]: val };
    // Auto-balance: adjust the other two proportionally
    const total = updated.name + updated.dob + updated.country;
    if (total !== 100) {
      const others = ['name', 'dob', 'country'].filter((f) => f !== field);
      const remaining = 100 - val;
      const otherTotal = others.reduce((s, f) => s + (weighting[f] || 0), 0);
      if (otherTotal > 0) {
        others.forEach((f) => {
          updated[f] = Math.round((weighting[f] / otherTotal) * remaining);
        });
        // Fix rounding
        const diff = 100 - (updated.name + updated.dob + updated.country);
        if (diff !== 0) updated[others[0]] += diff;
      } else {
        others.forEach((f, i) => {
          updated[f] = Math.round(remaining / others.length) + (i === 0 ? remaining % others.length : 0);
        });
      }
    }
    setWeighting(updated);
    updateConfig({ weighting: updated });
  };

  const selectAll = (group) => {
    const ids = group.datasets.map((d) => d.id);
    const allSelected = ids.every((id) => datasets.includes(id));
    let updated;
    if (allSelected) {
      updated = datasets.filter((d) => !ids.includes(d));
    } else {
      updated = [...new Set([...datasets, ...ids])];
    }
    setDatasets(updated);
    updateConfig({ datasets: updated });
  };

  return (
    <div className="sanctions-config">
      <div className="form-group">
        <label className="checkbox override-toggle">
          <input type="checkbox" checked={overrideGlobal} onChange={handleOverrideToggle} />
          <span>Override global screening settings</span>
        </label>
        {!overrideGlobal && (
          <div className="global-hint">Using tenant-level screening configuration from Settings.</div>
        )}
      </div>

      {overrideGlobal && (
        <>
          <div className="form-group">
            <label>Datasets ({datasets.length} selected)</label>
            <div className="dataset-groups">
              {DATASET_GROUPS.map((group) => {
                const groupSelected = group.datasets.filter((d) => datasets.includes(d.id)).length;
                return (
                  <div key={group.label} className="dataset-group">
                    <div className="group-header" onClick={() => selectAll(group)}>
                      <span className="group-name">{group.label}</span>
                      <span className="group-count">{groupSelected}/{group.datasets.length}</span>
                    </div>
                    <div className="group-datasets">
                      {group.datasets.map((ds) => (
                        <label key={ds.id} className="dataset-checkbox">
                          <input
                            type="checkbox"
                            checked={datasets.includes(ds.id)}
                            onChange={() => handleDatasetToggle(ds.id)}
                          />
                          <span className="ds-label">{ds.label}</span>
                          <span className="ds-region">{ds.region}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="form-group">
            <label>Match Threshold: {threshold}%</label>
            <input
              type="range"
              min={0}
              max={100}
              value={threshold}
              onChange={handleThresholdChange}
              className="threshold-slider"
            />
            <div className="threshold-labels">
              <span>Lenient (more matches)</span>
              <span>Strict (fewer matches)</span>
            </div>
          </div>

          <div className="form-group">
            <label>Field Weighting</label>
            <div className="weighting-hint">Controls how much each field contributes to match confidence. Must total 100%.</div>
            <div className="weighting-controls">
              <WeightSlider label="Name" value={weighting.name} onChange={(v) => handleWeightChange('name', v)} />
              <WeightSlider label="Date of Birth" value={weighting.dob} onChange={(v) => handleWeightChange('dob', v)} />
              <WeightSlider label="Country" value={weighting.country} onChange={(v) => handleWeightChange('country', v)} />
            </div>
            <div className="weighting-total">
              Total: {weighting.name + weighting.dob + weighting.country}%
            </div>
          </div>
        </>
      )}

      <style>{`
        .sanctions-config {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        .override-toggle span {
          font-weight: 500;
        }
        .global-hint {
          font-size: 11px;
          color: var(--text-secondary);
          margin-top: 4px;
          padding: 6px 8px;
          background: var(--bg-tertiary);
          border-radius: 4px;
        }
        .dataset-groups {
          display: flex;
          flex-direction: column;
          gap: 10px;
          max-height: 260px;
          overflow-y: auto;
        }
        .dataset-group {
          border: 1px solid var(--border-color);
          border-radius: 6px;
          overflow: hidden;
        }
        .group-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 6px 10px;
          background: var(--bg-tertiary);
          cursor: pointer;
          font-size: 12px;
          font-weight: 600;
        }
        .group-header:hover {
          background: var(--bg-hover);
        }
        .group-count {
          font-size: 11px;
          color: var(--text-secondary);
          font-weight: 400;
        }
        .group-datasets {
          padding: 6px 10px;
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        .dataset-checkbox {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 12px;
          cursor: pointer;
          padding: 2px 0;
        }
        .dataset-checkbox input {
          margin: 0;
        }
        .ds-label {
          flex: 1;
          color: var(--text-primary);
        }
        .ds-region {
          font-size: 10px;
          color: var(--text-secondary);
          background: var(--bg-tertiary);
          padding: 1px 5px;
          border-radius: 3px;
        }
        .threshold-slider {
          width: 100%;
          margin: 4px 0;
          accent-color: var(--accent-primary);
        }
        .threshold-labels {
          display: flex;
          justify-content: space-between;
          font-size: 10px;
          color: var(--text-secondary);
        }
        .weighting-hint {
          font-size: 11px;
          color: var(--text-secondary);
          margin-bottom: 8px;
        }
        .weighting-controls {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .weight-slider {
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .weight-slider .weight-label {
          width: 80px;
          font-size: 12px;
          color: var(--text-primary);
        }
        .weight-slider input[type="range"] {
          flex: 1;
          accent-color: var(--accent-primary);
        }
        .weight-slider .weight-value {
          width: 36px;
          text-align: right;
          font-size: 12px;
          font-weight: 600;
          color: var(--text-primary);
        }
        .weighting-total {
          text-align: right;
          font-size: 11px;
          color: var(--text-secondary);
          margin-top: 4px;
        }
      `}</style>
    </div>
  );
}

function WeightSlider({ label, value, onChange }) {
  return (
    <div className="weight-slider">
      <span className="weight-label">{label}</span>
      <input
        type="range"
        min={0}
        max={100}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      <span className="weight-value">{value}%</span>
    </div>
  );
}
